import { useState } from "react";
import {
  Box, Typography, Button, Chip, Tabs, Tab, Alert, useTheme,
  Breadcrumbs, Link, Paper,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";

import FactCheckIcon from "@mui/icons-material/FactCheck";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InventoryIcon from "@mui/icons-material/Inventory";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import FixedSizeDialog from "components/fixedSizeDialog";
import { useStockCountSession, useStockCountLines } from "api/hooks/inventory";
import { requester } from "api/pesabackend";

const VARIANCE_TABS = [
  { value: "all",      label: "All" },
  { value: "variance", label: "Variances" },
  { value: "accurate", label: "Accurate" },
];

const varianceChip = (type) => {
  if (type === "shortage")
    return <Chip label="Shortage" size="small" sx={{ fontSize: "11px", fontWeight: 600, backgroundColor: "rgba(192,57,43,0.12)", color: "#c0392b", border: "1px solid rgba(192,57,43,0.3)" }} />;
  if (type === "overage")
    return <Chip label="Overage" size="small" sx={{ fontSize: "11px", fontWeight: 600, backgroundColor: "rgba(230,126,34,0.12)", color: "#d35400", border: "1px solid rgba(230,126,34,0.3)" }} />;
  return <Chip label="Accurate" size="small" color="success" variant="outlined" sx={{ fontSize: "11px", fontWeight: 600 }} />;
};

const statusChip = (status) => {
  const map = {
    draft:     { label: "Draft",     color: "default" },
    completed: { label: "Completed", color: "warning" },
    posted:    { label: "Posted",    color: "success" },
  };
  const cfg = map[status] ?? { label: status, color: "default" };
  return <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" sx={{ fontSize: "12px", fontWeight: 600 }} />;
};

export default function StockTakeReviewForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;

  const sessionId = location.state?.sessionId;

  const { session, isLoading: isLoadingSession, refresh: refreshSession } =
    useStockCountSession(sessionId);

  const [tabValue, setTabValue] = useState("all");
  const {
    lines, isLoading: isLoadingLines, isLoadingMore, hasMore, loadMore,
  } = useStockCountLines(sessionId, tabValue);

  const [openConfirm, setOpenConfirm] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [postResult, setPostResult] = useState(null);
  const [postError, setPostError] = useState(null);

  const handlePostToGL = async () => {
    setIsPosting(true);
    setPostError(null);
    try {
      const res = await requester.post("/inventory/stock_count/post_accounting", {
        session_id: sessionId,
      });
      if (res.status === 201) {
        setPostResult(res.data.data.journal_entry_id);
        setOpenConfirm(false);
        refreshSession();
      }
    } catch (e) {
      const msg = e.response?.data?.error ?? "Failed to post to GL. Please try again.";
      setPostError(msg);
    } finally {
      setIsPosting(false);
    }
  };

  if (!sessionId) {
    return (
      <Box sx={{ p: "2rem" }}>
        <Alert severity="error">No session selected. Please navigate from Stock Take Sessions.</Alert>
        <Button sx={{ mt: "1rem" }} onClick={() => navigate("/inventory")}>
          Back to Inventory
        </Button>
      </Box>
    );
  }

  const canPost =
    session?.status === "completed" && !session?.posted_journal_id;
  const isPosted = session?.status === "posted" || !!session?.posted_journal_id;

  return (
    <Box>
      {postResult !== null && (
        <Alert sx={{ m: "15px" }} variant="filled" severity="success">
          Successfully posted to GL.{" "}
          {postResult
            ? `Journal Entry #${postResult} created.`
            : "No journal entries needed (no variances found)."}
        </Alert>
      )}
      {postError && (
        <Alert sx={{ m: "15px" }} severity="error">{postError}</Alert>
      )}

      <Box sx={{ padding: "20px", marginX: "20px" }}>
        <Breadcrumbs sx={{ padding: "20px" }} aria-label="breadcrumb">
          <Link underline="hover" sx={{ display: "flex", alignItems: "center" }} color="inherit"
            onClick={() => navigate("/inventory")}>
            <InventoryIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Inventory
          </Link>
          <Link underline="hover" sx={{ display: "flex", alignItems: "center" }} color="inherit"
            onClick={() => navigate("/inventory")}>
            Stock Take Sessions
          </Link>
          <Typography sx={{ display: "flex", alignItems: "center" }} color="text.primary">
            Review
          </Typography>
        </Breadcrumbs>

        {/* ── Session header ── */}
        {!isLoadingSession && session && (
          <Paper sx={{ p: "1.5rem 2rem", mb: "1.5rem" }} elevation={2}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Box sx={{ width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
                  <FactCheckIcon sx={{ fontSize: "22px" }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "20px", fontWeight: 600, color: textPri }}>
                    {session.reference}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
                    Count Date: {moment(session.count_date).format("LL")}
                    {" · "}Added by {session.added_by?.first_name} {session.added_by?.last_name}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                {statusChip(session.status)}
                {canPost && (
                  <Button
                    onClick={() => setOpenConfirm(true)}
                    startIcon={<AccountBalanceIcon sx={{ fontSize: "16px" }} />}
                    sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}
                  >
                    Post to GL
                  </Button>
                )}
                {isPosted && session.posted_journal_id && (
                  <Typography sx={{ fontSize: "13px", color: accent, fontWeight: 500 }}>
                    Journal #{session.posted_journal_id}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* summary stats */}
            <Box sx={{ display: "flex", gap: "2rem", mt: "1.5rem", flexWrap: "wrap" }}>
              {[
                { label: "Total Lines", value: session.lines_total },
                { label: "Variances", value: session.lines_with_variance, highlight: session.lines_with_variance > 0 },
                { label: "Accurate", value: session.lines_accurate },
              ].map((s) => (
                <Box key={s.label}>
                  <Typography sx={{ fontSize: "11px", color: textSec, textTransform: "uppercase", letterSpacing: "0.07em" }}>{s.label}</Typography>
                  <Typography sx={{ fontSize: "24px", fontWeight: 600, color: s.highlight ? "#c0392b" : accent }}>{s.value}</Typography>
                </Box>
              ))}
            </Box>

            {session.detail && (
              <Typography sx={{ mt: "1rem", fontSize: "13px", color: textSec }}>
                {session.detail}
              </Typography>
            )}
          </Paper>
        )}

        {/* ── Tabs ── */}
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}
          sx={{ mb: "1rem", "& .MuiTab-root": { fontSize: "13px", textTransform: "none", fontWeight: 500 } }}>
          {VARIANCE_TABS.map((t) => (
            <Tab key={t.value} value={t.value} label={
              t.value === "variance" && session
                ? `${t.label} (${session.lines_with_variance})`
                : t.value === "accurate" && session
                ? `${t.label} (${session.lines_accurate})`
                : t.label
            } />
          ))}
        </Tabs>

        {/* ── Lines table ── */}
        <ReportTable
          columns={[
            { label: "Product" },
            { label: "Unit" },
            { label: "Batch" },
            { label: "System Qty", align: "right" },
            { label: "Counted Qty", align: "right" },
            { label: "Variance Qty", align: "right" },
            { label: "Variance Value", align: "right" },
            { label: "Type" },
          ]}
          rows={lines}
          loading={isLoadingLines}
          hasMore={hasMore}
          onLoadMore={loadMore}
          loadingMore={isLoadingMore}
          emptyMessage="No lines found"
          renderRow={(row, index) => {
            const isVariance = row.has_variance;
            const rowBg = isVariance
              ? (row.variance_type === "shortage"
                  ? (isDark ? "rgba(192,57,43,0.07)" : "rgba(192,57,43,0.04)")
                  : (isDark ? "rgba(230,126,34,0.07)" : "rgba(230,126,34,0.04)"))
              : undefined;
            return (
              <RRow key={row.id} index={index}>
                <RCell style={{ backgroundColor: rowBg }}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                    {row.related_product?.product_name}
                  </Typography>
                </RCell>
                <RCell style={{ backgroundColor: rowBg }}>
                  {row.related_product_unit?.related_business_product_unit?.name ?? "—"}
                </RCell>
                <RCell style={{ backgroundColor: rowBg }}>{row.batch || "—"}</RCell>
                <RCell align="right" style={{ backgroundColor: rowBg }}>
                  {parseFloat(row.system_quantity).toFixed(2)}
                </RCell>
                <RCell align="right" style={{ backgroundColor: rowBg }}>
                  {parseFloat(row.counted_quantity).toFixed(2)}
                </RCell>
                <RCell align="right" style={{ backgroundColor: rowBg }}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 500,
                    color: parseFloat(row.variance_quantity) < 0 ? "#c0392b"
                         : parseFloat(row.variance_quantity) > 0 ? "#d35400" : "inherit" }}>
                    {parseFloat(row.variance_quantity) > 0 ? "+" : ""}{parseFloat(row.variance_quantity).toFixed(2)}
                  </Typography>
                </RCell>
                <RCell align="right" style={{ backgroundColor: rowBg }}>
                  {parseFloat(row.variance_value).toFixed(2)}
                </RCell>
                <RCell style={{ backgroundColor: rowBg }}>
                  {varianceChip(row.variance_type)}
                </RCell>
              </RRow>
            );
          }}
        />
      </Box>

      {/* ── Post to GL confirmation dialog ── */}
      <FixedSizeDialog
        open={openConfirm}
        handleClose={() => setOpenConfirm(false)}
        dialogHeading="Post variances to General Ledger?"
        negativeResponse="Cancel"
        positiveResponse="Post to GL"
        handleSubmit={handlePostToGL}
        isSubmitting={isPosting}
        isSubmittingText="Posting…"
        maxWidth="sm"
        content={
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", p: "0.875rem 1rem",
            backgroundColor: "rgba(226,75,74,0.07)", border: "1px solid rgba(226,75,74,0.2)", borderRadius: "8px" }}>
            <WarningAmberIcon sx={{ fontSize: "18px", color: "#e24b4a", flexShrink: 0, mt: "1px" }} />
            <Typography sx={{ fontSize: "13px", color: isDark ? "#f09595" : "#a32d2d", lineHeight: 1.6 }}>
              This will create journal entries for all variance lines (shortages and overages) and mark this session as
              <strong> posted</strong>. This action is <strong>irreversible</strong>. Are you sure you want to proceed?
            </Typography>
          </Box>
        }
      />
    </Box>
  );
}
